// The event RSVP form — one definition for every template.
//
// It was 230 lines inside Blocks/EventSignupBlock.vue, and a second
// template drawing the same form would have meant a second copy of
// the Turnstile handshake, the URL-flash round trip, the capacity
// arithmetic and the occurrence picker. Two copies of that is the
// four-grade-baselines problem with somebody's place on a trail crew
// in the middle of it: a template that read capacity differently
// would tell one visitor they were in and the other they were
// waitlisted, for the same event, at the same moment.
//
// A template draws the fields and nothing else. Everything about
// where the post goes, what comes back and what the visitor is told
// lives here.

import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { sitePrefix } from './siteLocation.js';
import { TURNSTILE_LOOK, waitForToken } from './useSiteForm.js';
import { cleanQuantities, needsMemberCode, ticketCount, ticketTotal } from './eventTickets.js';
import { priceText } from './shop.js';

// Where the page is being served decides the URL: /site/{org}/… on
// the app host, /… on the organization's own domain. Show.vue's link
// rewriter only handles <a> clicks, not a form POST, so the prefix is
// worked out here, in siteLocation.js.
export { sitePrefix };

const TURNSTILE_SCRIPT_ID = 'tiq-cf-turnstile';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function ensureTurnstileScript() {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') return resolve(null);
        if (window.turnstile) return resolve(window.turnstile);
        const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
        if (existing) {
            existing.addEventListener('load', () => resolve(window.turnstile));
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.id = TURNSTILE_SCRIPT_ID;
        s.src = TURNSTILE_SCRIPT_URL;
        s.async = true; s.defer = true;
        s.onload = () => resolve(window.turnstile);
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

// Per-field config from the controller. The defaults match the
// original full form, so an event saved before the field settings
// existed looks exactly as it did.
const FIELD_DEFAULTS = {
    name_required: true,
    email_visible: true, email_required: true,
    phone_visible: true, phone_required: false,
    party_size_visible: true,
    notes_visible: true, notes_required: false,
};

/**
 * @param {Function|Object} configSource the block's config — a getter
 *        (`() => props.config`) or a ref. Only the copy fallbacks are
 *        read from it; everything else comes from the event.
 */
export function useEventSignup(configSource = () => ({})) {
    const config = computed(() => {
        const c = typeof configSource === 'function' ? configSource() : (configSource?.value ?? configSource);
        return c || {};
    });

    const event = inject('tiqCurrentEvent', null);
    const page = usePage();

    const signupCfg = computed(() => event?.signups || { enabled: false });
    const isRecurring = computed(() => !!event?.is_recurring);
    const occurrences = computed(() => event?.upcoming_occurrences || []);

    // Selected occurrence for recurring events. Defaults to the next
    // upcoming one so a hurried visitor doesn't have to pick.
    const selectedDate = ref(occurrences.value[0] || '');
    watch(occurrences, (next) => { if (!selectedDate.value && next[0]) selectedDate.value = next[0]; });

    // Capacity per the selected occurrence.
    const taken = computed(() => {
        const t = signupCfg.value.taken;
        if (typeof t === 'number') return t;       // single events
        if (t && selectedDate.value) return Number(t[selectedDate.value] || 0);
        return 0;
    });
    const waitlistTaken = computed(() => {
        const w = signupCfg.value.waitlist;
        if (typeof w === 'number') return w;
        if (w && selectedDate.value) return Number(w[selectedDate.value] || 0);
        return 0;
    });
    const capacity = computed(() => Number(signupCfg.value.capacity) || null);
    const remaining = computed(() => capacity.value ? Math.max(0, capacity.value - taken.value) : null);
    const isFull = computed(() => remaining.value !== null && remaining.value === 0);
    const deadlinePassed = computed(() => {
        if (!signupCfg.value.deadline) return false;
        return new Date(signupCfg.value.deadline).getTime() < Date.now();
    });

    // Capacity-full no longer closes the form — it flips it to
    // waitlist mode. Only the manager-set deadline + the enabled
    // toggle actually close it.
    const closedReason = computed(() => {
        if (!signupCfg.value.enabled) return 'Signups are not open for this event.';
        if (deadlinePassed.value) return 'The signup deadline has passed.';
        if (tickets.value && !tickets.value.on_sale) return "Tickets for this event aren't on sale online right now.";
        return null;
    });

    // Form state.
    const name = ref('');
    const email = ref('');
    const phone = ref('');
    const partySize = ref(1);
    const notes = ref('');
    const waiverAgreed = ref(false);
    const submitting = ref(false);

    // ── Tickets ───────────────────────────────────────────────────
    // A paid event: the form picks how many of each ticket and the
    // server sends the visitor to Stripe to pay (EventSignupController
    // ::checkoutTickets). Null for a free event, which is unchanged.
    const tickets = computed(() => signupCfg.value.tickets || null);
    const ticketQty = ref({});
    const memberCode = ref('');
    const ticketsCount = computed(() => (tickets.value ? ticketCount(tickets.value.types, ticketQty.value) : 0));
    const ticketsTotal = computed(() => (tickets.value ? ticketTotal(tickets.value.types, ticketQty.value) : 0));
    const askMemberCode = computed(() => needsMemberCode(tickets.value, ticketQty.value));
    const money = (n) => priceText(String(n), tickets.value?.currency || 'USD');
    // There is no waitlist for a place somebody would pay for.
    const soldOut = computed(() => !!tickets.value && isFull.value);
    const ticketsTooMany = computed(() => !!tickets.value && remaining.value !== null && ticketsCount.value > remaining.value);
    function setTicketQty(key, n) {
        if (!tickets.value) return;
        ticketQty.value = cleanQuantities(tickets.value.types, { ...ticketQty.value, [key]: n }, tickets.value.max_per_type || 20);
    }

    const waiverEnabled = computed(() => !!signupCfg.value.waiver_enabled);
    const waiverText = computed(() => signupCfg.value.waiver_text || '');
    // An uploaded waiver outranks the text — the editor saves one or
    // the other, and the form shows it in WaiverDocument's reader.
    const waiverFile = computed(() => signupCfg.value.waiver_file || null);

    // ── Cloudflare Turnstile ──────────────────────────────────────
    // Lazy-loads the script + renders the challenge widget when the
    // org has configured a site key. Same pattern as Auth/Register.vue.
    const turnstileSiteKey = computed(() => signupCfg.value.turnstile_site_key || null);
    const turnstileToken = ref('');
    const turnstileContainer = ref(null);
    let turnstileWidgetId = null;

    onMounted(async () => {
        if (!turnstileSiteKey.value) return;
        try {
            const turnstile = await ensureTurnstileScript();
            if (!turnstile || !turnstileContainer.value) return;
            turnstileWidgetId = turnstile.render(turnstileContainer.value, {
                sitekey: turnstileSiteKey.value,
                callback: (token) => { turnstileToken.value = token; },
                'error-callback': () => { turnstileToken.value = ''; },
                'expired-callback': () => { turnstileToken.value = ''; },
                ...TURNSTILE_LOOK,
            });
        } catch {
            // Script blocked / offline — fail open. The server-side rule
            // does the same when TURNSTILE_SECRET isn't configured.
        }
    });
    onBeforeUnmount(() => {
        if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetId !== null) {
            try { window.turnstile.remove(turnstileWidgetId); } catch { /* ignore */ }
        }
    });

    // Tokens are single-use, so one is re-issued after every attempt
    // — a visitor signing a friend up right afterwards would
    // otherwise meet an "already used" rejection from Cloudflare.
    function resetTurnstile() {
        if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetId !== null) {
            try { window.turnstile.reset(turnstileWidgetId); } catch { /* ignore */ }
        }
        turnstileToken.value = '';
    }

    // Signup result is round-tripped via URL query params after a 303
    // redirect (?signup=success|waitlisted|error&signup_msg=...&
    // signup_err_fields=name,email). Bypasses session entirely so it
    // works on vanity domains too, and produces a real reloadable URL.
    const urlFlash = ref(null);
    const urlErrorFields = ref({});
    function readFlashFromUrl() {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const level = params.get('signup');
        const msg = params.get('signup_msg');
        const fieldsCsv = params.get('signup_err_fields');
        if (level && msg) {
            urlFlash.value = { message: msg, level };
            if (fieldsCsv) {
                urlErrorFields.value = Object.fromEntries(
                    fieldsCsv.split(',').filter(Boolean).map((f) => [f, 'Required.']),
                );
            }
            // Strip the signup-related keys from the URL so a reload
            // doesn't re-show the toast forever.
            params.delete('signup');
            params.delete('signup_msg');
            params.delete('signup_err_fields');
            const q = params.toString();
            window.history.replaceState({}, '',
                window.location.pathname + (q ? '?'+q : '') + window.location.hash);
        }
    }
    onMounted(readFlashFromUrl);
    // Inertia keeps the block mounted across navigations on the same
    // page component, so onMounted only fires on the first GET. Watch
    // the page URL too so the post-submit redirect (?signup=success…)
    // triggers the read.
    watch(() => page.url, readFlashFromUrl);

    const flashFromPayload = computed(() => signupCfg.value.flash || null);
    const flashMsg = computed(() =>
        urlFlash.value?.message || flashFromPayload.value?.message || page.props.flash?.message || null);
    const flashLevel = computed(() =>
        urlFlash.value?.level || flashFromPayload.value?.level || page.props.flash?.level || 'success');
    const submitErrors = computed(() =>
        Object.keys(urlErrorFields.value).length ? urlErrorFields.value
            : (flashFromPayload.value?.errors || page.props.errors || {}));

    async function submit() {
        if (!event) return;
        submitting.value = true;
        if (turnstileSiteKey.value && turnstileWidgetId !== null) await waitForToken(turnstileToken);
        const url = `${sitePrefix()}/events/${event.slug}/signup`;
        router.post(url, {
            name: name.value,
            email: email.value,
            phone: phone.value || null,
            party_size: Number(partySize.value) || 1,
            notes: notes.value || null,
            occurrence_date: isRecurring.value ? selectedDate.value : null,
            waiver_agreed: waiverAgreed.value,
            tickets: tickets.value ? cleanQuantities(tickets.value.types, ticketQty.value) : undefined,
            member_code: askMemberCode.value ? memberCode.value : undefined,
            'cf-turnstile-response': turnstileToken.value || '',
        }, {
            preserveScroll: true,
            onSuccess: () => {
                // Reset on success — flash message confirms.
                name.value = '';
                email.value = '';
                phone.value = '';
                partySize.value = 1;
                notes.value = '';
                waiverAgreed.value = false;
                resetTurnstile();
            },
            onError: resetTurnstile,
            onFinish: () => (submitting.value = false),
        });
    }

    // "Add to calendar" — the single-event .ics, the same prefix rule
    // as the POST above.
    const icsUrl = computed(() => (event?.slug ? `${sitePrefix()}/events/${event.slug}.ics` : null));

    // Per-event overrides take precedence over the template block's
    // config. Lets a workday say "Sign up to help" while a race
    // reuses the same template with "Register now" — without needing
    // separate templates per event type.
    const eyebrow = computed(() => signupCfg.value.eyebrow_override ?? (config.value.eyebrow || ''));
    const heading = computed(() => signupCfg.value.heading_override ?? (config.value.heading || 'Sign up'));
    const subtitle = computed(() => signupCfg.value.subtitle_override ?? (config.value.subtitle || ''));
    const buttonLabel = computed(() => {
        if (tickets.value) {
            return ticketsCount.value > 0 ? `Continue to payment · ${money(ticketsTotal.value)}` : 'Choose your tickets';
        }
        return signupCfg.value.button_label_override ?? (config.value.button_label || 'Count me in');
    });

    const fields = computed(() => ({ ...FIELD_DEFAULTS, ...(signupCfg.value.field_config || {}) }));

    return {
        event,
        signupCfg,
        isRecurring, occurrences, selectedDate,
        taken, waitlistTaken, capacity, remaining, isFull, deadlinePassed, closedReason,
        name, email, phone, partySize, notes, waiverAgreed, submitting,
        waiverEnabled, waiverText, waiverFile,
        turnstileSiteKey, turnstileContainer,
        flashMsg, flashLevel, submitErrors,
        submit, icsUrl,
        eyebrow, heading, subtitle, buttonLabel, fields,
        tickets, ticketQty, setTicketQty, memberCode, ticketsCount, ticketsTotal, askMemberCode, soldOut, ticketsTooMany, money,
    };
}
